/**
 * "Re-optimize keeping my changes": derive fixedVehicleId pins from the
 * manual-edit history so a re-solve preserves the user's placements while the
 * solver repairs everything else.
 *
 * Contract (docs/frontend-request-fixed-assignments.md addendum, implemented by
 * the backend on develop):
 *  - pin ONLY entities the user manually PLACED — amendments of type 'move' or
 *    'assign'. Untouched trips float so the solver can repair around the edits.
 *  - the pin is the entity's CURRENT vehicle (a later move supersedes an
 *    earlier one); an entity since unassigned gets no pin (the solver may
 *    legitimately re-add it — drop penalties already discourage that).
 *  - 'reorder' is not pinnable: the backend pins vehicles, not sequences. The
 *    caller should surface that honestly rather than pretend order survives.
 *  - vehicle-routing edits pin the LOCATION (Location.fixedVehicleId); trips
 *    from the trips table pin the TRIP. route_stops rows carry synthesized
 *    tripIds in VR, so "is it a real trip" = "is tripId in the trips table".
 */

import { groupRouteStops } from './evaluator';

const PLACING_TYPES = new Set(['move', 'assign']);

/**
 * @returns {{ tripPins: Map<string,string>, locationPins: Map<string,string>,
 *             unpinnableReorders: number }}
 */
export function computePinnedAssignments(data) {
  const amendments = Array.isArray(data.resultAmendments) ? data.resultAmendments : [];
  const tripIdsInTable = new Set((data.trips || []).map((t) => String(t.UUID)));
  const byVehicle = groupRouteStops(data.route_stops);

  // Current vehicle per entity, from the live rows.
  const currentVehicleByTrip = new Map();
  const currentVehicleByLocation = new Map();
  for (const [vehicleId, rows] of byVehicle) {
    for (const row of rows) {
      if (row.stopType === 'route_start' || row.stopType === 'route_end') continue;
      if (row.tripId != null) currentVehicleByTrip.set(String(row.tripId), vehicleId);
      if (row.locationId != null) currentVehicleByLocation.set(String(row.locationId), vehicleId);
    }
  }

  const tripPins = new Map();
  const locationPins = new Map();
  let unpinnableReorders = 0;

  for (const amendment of amendments) {
    const payload = amendment.payload || {};
    if (amendment.type === 'reorder') {
      unpinnableReorders += 1;
      continue;
    }
    if (!PLACING_TYPES.has(amendment.type)) continue;

    const tripId = payload.tripId != null ? String(payload.tripId) : null;
    if (tripId && tripIdsInTable.has(tripId)) {
      const vehicleId = currentVehicleByTrip.get(tripId);
      if (vehicleId != null) tripPins.set(tripId, vehicleId);
      else tripPins.delete(tripId); // placed then unassigned — no pin
      continue;
    }
    const locationId =
      payload.locationId != null ? String(payload.locationId) : null;
    if (locationId) {
      const vehicleId = currentVehicleByLocation.get(locationId);
      if (vehicleId != null) locationPins.set(locationId, vehicleId);
      else locationPins.delete(locationId);
    }
  }

  return { tripPins, locationPins, unpinnableReorders };
}

/**
 * Stamp the pins onto an already-built ProblemRequest (mutates in place and
 * returns it). Model instances serialize own properties, so assignment is
 * sufficient; the generated client also declares fixedVehicleId since the
 * develop regeneration.
 */
export function applyPinsToProblem(problem, { tripPins, locationPins }) {
  const trips = problem?.problemData?.trips || [];
  for (const trip of trips) {
    const pin = trip.tripId != null ? tripPins.get(String(trip.tripId)) : null;
    if (pin != null) trip.fixedVehicleId = pin;
  }
  const locations = problem?.problemData?.locations || [];
  for (const location of locations) {
    const pin = location.locationId != null ? locationPins.get(String(location.locationId)) : null;
    if (pin != null) location.fixedVehicleId = pin;
  }
  return problem;
}
